/**
 * Wake Word Intelligence — Layer 2 of Aria's "Should I Respond?" system.
 *
 * Instead of simple substring matching ("does the transcript contain 'aria'?"),
 * this module analyzes HOW "Aria" appears in the utterance to decide whether
 * the user is talking TO Aria or just ABOUT her.
 *
 * "Hey Aria, what time is it?" → directed (respond)
 * "I told Aria about it yesterday" → not directed (stay quiet)
 * "Aria" (alone) → directed (user calling for attention)
 */

// ── Wake word variants (transcription errors included) ──────────────────────
// Whisper / Web Speech mis-hear "Aria" as a surprising number of things —
// every user pronounces it slightly differently and Whisper hears their
// unique vocal signature differently again. This list covers the realistic
// space of mis-transcriptions; additional per-user variants are merged in
// at runtime (see USER_LEARNED_VARIANTS / setUserVariants below).
//
// Owner spec 2026-05-02: "we tell the user how it's supposed to sound but
// our AI needs to pick up on how the user tries to get the attention of
// the AI" — so the matcher is permissive and a per-user learner promotes
// whichever variant THIS user actually uses.
const DEFAULT_WAKE_VARIANTS = [
  // Canonical and direct misses
  'aria', 'area', 'arya', 'ariel', 'ari', 'aaria',
  'aria\'s', 'arya\'s',
  // "Ariah" pronunciation (owner spec) — Whisper often transcribes as -h forms
  'ariah', 'aria-h', 'ariya', 'ariyah', 'arija', 'aaryah',
  // Phonetic neighbors (different vowel layouts)
  'aurea', 'aurya', 'aurora', 'orea', 'orya', 'oreo',
  // Hard-r front: "are-ee-uh" tokenized weirdly
  'are ya', 'are you a', 'are ya a', 'aree-ah', 'airy-uh', 'airy', 'airie',
  // First-name confusions
  'maria', 'mariah', 'mariyah', 'erin', 'aaron', 'aaryah',
  // Short-form / mumble
  'rea', 'rya', 'ree', 'ree-uh', 'rear', 'urea',
];
let USER_LEARNED_VARIANTS = []; // populated at runtime via setUserVariants()
const WAKE_PREFIXES = ['hey', 'ok', 'okay', 'yo', 'excuse me', 'hi', 'hello', 'um', 'uh', 'so'];

// Tokens shorter than this are ignored by the fuzzy match (avoids false positives on "a", "i", etc.)
const FUZZY_MIN_TOKEN_LEN = 3;
// Max edit distance from "aria" for a token to count as a fuzzy wake match.
// Bumped from 1 → 2 to accommodate the user-pronunciation diversity that
// Whisper produces (e.g. "ariyah" → distance 2, "ariel" → distance 2,
// "aurea" → distance 2). The length + shape guards in hasFuzzyWake()
// prevent unrelated 4-letter words like "more" or "rain" from matching.
const FUZZY_MAX_DISTANCE = 2;

/** Levenshtein edit distance. Small; sufficient for tiny wake-word tokens. */
function editDistance(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const m = a.length, n = b.length;
  let prev = new Array(n + 1);
  let curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        curr[j - 1] + 1,
        prev[j] + 1,
        prev[j - 1] + cost
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}

/**
 * True if any token in the transcript looks like the user trying to say
 * "Aria". Uses BOTH edit distance (typos / phonetic shifts) AND a shape
 * guard (must look aria-shaped — vowel-r-vowel pattern or known suffix)
 * so that random 4-letter words like "more" or "rain" don't false-match.
 *
 * Edit-distance 2 is permissive — it lets "ariyah", "ariel", "ariya",
 * "aurea" through. The shape guard is what keeps it sane.
 */
function hasFuzzyWake(lower) {
  const tokens = lower.split(/[\s,.!?;:]+/).filter(Boolean);
  for (const tok of tokens) {
    if (tok.length < FUZZY_MIN_TOKEN_LEN) continue;
    if (tok.length > 8) continue; // "atrocity" shouldn't match by edit distance
    const dist = editDistance(tok, 'aria');
    if (dist === 0) return true; // exact
    if (dist === 1) return true; // one-edit neighbours: aria, area, ariax, raia
    // Distance 2: only if the token has aria-shape characteristics — starts
    // with vowel-r OR ends in a known aria-suffix. Otherwise too noisy.
    if (dist <= FUZZY_MAX_DISTANCE) {
      if (/^[aeiouy]r/.test(tok)) return true;        // ariel, area, aria, aurea
      if (/(rya|ria|riah|iah|riyah|rea)$/.test(tok)) return true; // arya, ariah, ariyah
    }
  }
  return false;
}

/**
 * Merge in the user's personally-learned wake-word variants. Called by
 * the desktop / web app after fetching the user's profile at boot. The
 * server tracks how THIS user actually says "Aria" and promotes
 * frequently-used variants to a learned list — passing them here adds
 * them to the matcher.
 *
 * @param {string[]} variants - tokens like ['ariah', 'arya', 'ariya']
 */
export function setUserVariants(variants) {
  if (!Array.isArray(variants)) return;
  USER_LEARNED_VARIANTS = variants
    .filter((v) => typeof v === 'string' && v.length >= 2 && v.length <= 12)
    .map((v) => v.toLowerCase().trim());
  // Force the cached regex group + pattern arrays to rebuild on next call
  // so the learned variants flow into directed-context scoring.
  if (typeof invalidateVariantCache === 'function') invalidateVariantCache();
  _directed = null;
  _notDirected = null;
}

/**
 * Get the current effective variant list — defaults + per-user learned.
 * Used by isDirectedAtAria and stripWakeWord so they see learned variants.
 */
function getEffectiveVariants() {
  if (!USER_LEARNED_VARIANTS.length) return DEFAULT_WAKE_VARIANTS;
  // Dedupe, learned-first so user's preferred form takes priority
  return [...new Set([...USER_LEARNED_VARIANTS, ...DEFAULT_WAKE_VARIANTS])];
}

// ── Directed-at-assistant patterns (no name needed). These signal the user is
//     talking TO an assistant — help requests, questions, imperatives aimed at "you".
//     Keep tight enough to ignore ambient chatter and TV.
// Optional filler prefix users often say before the real request.
// Examples: "All right, can you hear me?" / "So, what's the weather?" / "Hey, help me out."
const FILLER_PREFIX = /^(?:all\s+right[,.]?\s+|alright[,.]?\s+|so[,.]?\s+|well[,.]?\s+|ok(?:ay)?[,.]?\s+|hey[,.]?\s+|yo[,.]?\s+|um[,.]?\s+|uh[,.]?\s+|hi[,.]?\s+|hello[,.]?\s+)?/i;
const sop = (rest) => new RegExp('^' + FILLER_PREFIX.source.replace(/^\^|\$$/g, '') + rest, 'i');

const DIRECTED_NO_NAME_PATTERNS = [
  // Help requests: "can you help me", "help me with", "i need help"
  /\b(?:can|could|would|will|do|did)\s+you\s+(?:help|assist|do|make|tell|show|find|search|look|give|explain|explain\s+me)\b/i,
  /\b(?:help|assist)\s+me\b/i,
  /\bi\s+(?:need|want|could\s+use)\s+(?:some\s+|your\s+)?(?:help|assistance)\b/i,
  // "can you hear/see/understand me" — canonical "are you listening" phrases
  /\b(?:can|could|do|did|are)\s+you\s+(?:hear|see|understand|get|catch)\s+(?:me|that|this|what)\b/i,
  /\b(?:are|were)\s+you\s+(?:there|listening|awake|ready|online|on)\b/i,
  // Direct questions to the assistant: "can you X", "do you know" — allow filler prefix
  sop('(?:can|could|would|will)\\s+you\\b'),
  sop('(?:do|does|did)\\s+you\\s+(?:know|have|think|see|understand|remember)\\b'),
  sop('(?:are|were)\\s+you\\b'),
  // Imperatives addressed to the assistant
  sop('(?:tell|show|find|search|look\\s+up|explain|open|play|set|stop|pause|remind)\\s+me\\b'),
  sop('(?:tell|show|find|search|look\\s+up|explain|open|play|set)\\s+(?:it|us|the)\\b'),
  // Information-seeking questions that only make sense directed at an assistant
  sop("(?:what'?s|what\\s+is|what\\s+are|how\\s+do\\s+i|how\\s+can\\s+i|how\\s+do\\s+you|why\\s+is|why\\s+are|when\\s+is|when\\s+are|where\\s+is|where\\s+are|who\\s+is|who\\s+are)\\b"),
];

// ── Non-directed patterns even if they contain directed-looking words.
//     e.g. "I was telling John how to..." — not directed at assistant.
const NON_DIRECTED_GUARDS = [
  /\b(?:told|telling|said|saying|asked|asking)\s+(?:him|her|them|john|jane|my|his|her)\b/i,
  /\b(?:i\s+was|we\s+were|he\s+was|she\s+was|they\s+were)\s+(?:just\s+)?(?:telling|saying|asking)\b/i,
];

/** True if the utterance looks directed at the assistant even without the wake word. */
function isDirectedByPhrasing(text) {
  if (!text) return false;
  const lower = text.toLowerCase().trim();
  for (const guard of NON_DIRECTED_GUARDS) {
    if (guard.test(lower)) return false;
  }
  for (const pattern of DIRECTED_NO_NAME_PATTERNS) {
    if (pattern.test(lower)) return true;
  }
  return false;
}

// ── Variant alternation group ───────────────────────────────────
// Built once from the effective variant list — used inside DIRECTED /
// NOT_DIRECTED patterns. Rebuild whenever setUserVariants() is called so
// learned variants flow into the phrase-shape matching.
let _variantAlt = null;
function getVariantAlt() {
  if (_variantAlt) return _variantAlt;
  // Sort longest-first so "ariyah" matches before "aria" (which is a
  // prefix). Escape RegExp special chars (apostrophes etc.).
  const variants = getEffectiveVariants()
    .slice()
    .sort((a, b) => b.length - a.length)
    .map((v) => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  _variantAlt = variants.join('|');
  return _variantAlt;
}
function invalidateVariantCache() { _variantAlt = null; }

// ── Directed patterns: user is talking TO Aria ──────────────────────────────
// Built lazily so user-learned variants get baked in. Rebuilt on demand
// when setUserVariants() updates the list.
let _directed = null;
function getDirectedPatterns() {
  if (_directed) return _directed;
  const v = getVariantAlt();
  _directed = [
    { re: new RegExp(`^(?:hey|ok|okay|yo|hi|excuse\\s+me)?\\s*(?:${v})[,.\\s!]*`, 'i'), weight: 0.9, reason: 'vocative_start' },
    { re: new RegExp(`(?:${v})[,.]?\\s+(?:can|could|would|will|should|do|don'?t|tell|help|show|find|search|create|make|set|turn|play|stop|pause|open|close|send|read|write|check|look\\s+up|explain|what|how|why|who|when|where|is\\s+there|are\\s+there)`, 'i'), weight: 0.85, reason: 'followed_by_request' },
    { re: new RegExp(`(?:right|okay|ok|correct|yeah|yes|no|huh)[,.]?\\s*(?:${v})\\s*[?.]?\\s*$`, 'i'), weight: 0.8, reason: 'tag_question' },
    { re: new RegExp(`^\\s*(?:hey\\s+)?(?:${v})[,.\\s!?]*$`, 'i'), weight: 0.95, reason: 'solo_callout' },
    { re: new RegExp(`(?:let\\s+me\\s+ask|hold\\s+on.*?ask|i'?ll\\s+ask|ask|asking)\\s+(?:${v})`, 'i'), weight: 0.9, reason: 'delegation' },
    { re: new RegExp(`(?:so|but|and|well|now)\\s+(?:${v})[,.]?\\s+(?:what|how|can|could|would|do|tell|help)`, 'i'), weight: 0.8, reason: 'mid_sentence_address' },
    { re: new RegExp(`^(?:${v})[,.]?\\s+(?:stop|cancel|go|wait|hold|never\\s*mind|shut|quiet|pause|resume|continue|repeat|again)`, 'i'), weight: 0.9, reason: 'imperative' },
  ];
  return _directed;
}

// ── NOT directed patterns: user is talking ABOUT Aria ───────────────────────
let _notDirected = null;
function getNotDirectedPatterns() {
  if (_notDirected) return _notDirected;
  const v = getVariantAlt();
  _notDirected = [
    { re: new RegExp(`(?:told|telling|asked|mentioned\\s+to|said\\s+to|texted|messaged|emailed)\\s+(?:${v})\\s+(?:about|that|to|if|whether|the|a)`, 'i'), weight: 0.85, reason: 'reporting_speech' },
    { re: new RegExp(`(?:with|to|from|about|like|than|for|near|beside|behind|without)\\s+(?:${v})(?:\\s|$|[,.])`, 'i'), weight: 0.7, reason: 'object_of_preposition' },
    { re: new RegExp(`(?:${v})(?:'?s)\\s+(?:not|a|the|my|your|his|her|their|an|been|going|always|never|already|just|still|only|also|really|pretty|very|quite|too)`, 'i'), weight: 0.75, reason: 'possessive_third_person' },
    { re: new RegExp(`(?:I|we|he|she|they|you|it|who|someone|nobody|everybody)\\s+(?:\\w+\\s+){0,4}(?:${v})\\s+(?:that|about|to|the|a|this|some|how|what|when|why)`, 'i'), weight: 0.65, reason: 'third_person_reference' },
    { re: new RegExp(`(?:what|who|how)\\s+(?:is|was|does|did|has|can|will)\\s+(?:${v})(?:\\s|$|[?.,])`, 'i'), weight: 0.6, reason: 'talking_about' },
    { re: new RegExp(`(?:and|,)\\s+(?:${v})\\s*(?:and|,)`, 'i'), weight: 0.7, reason: 'name_in_list' },
  ];
  return _notDirected;
}


// ── Engagement follow-up patterns (valid without wake word when attentive) ──
const FOLLOWUP_PATTERNS = [
  /^(?:what|how|why|who|when|where|which|can|could|would|should|is|are|do|does|did|will|have|has)\b/i,
  /^(?:tell|explain|help|show|find|create|make|set|turn|play|stop|open|close|send|search|look\s+up|check)\b/i,
  /^(?:yes|yeah|yep|no|nah|nope|sure|okay|ok|right|correct|exactly|definitely|maybe|probably)\b/i,
  /^(?:please|thanks|thank\s+you)\b/i,
];

/**
 * Classify whether a transcript containing "Aria" is directed at Aria or not.
 *
 * @param {string} transcript — The raw transcript text
 * @param {'attentive'|'sleeping'} ariaAttentionState — Current attention state
 * @returns {{ isDirected: boolean, confidence: number, reason: string, isDelegation: boolean }}
 */
export function classifyWakeWord(transcript, ariaAttentionState = 'sleeping') {
  if (!transcript || typeof transcript !== 'string') {
    return { isDirected: false, confidence: 0, reason: 'empty_transcript', isDelegation: false };
  }

  const text = transcript.trim();
  const lower = text.toLowerCase();

  // Check if any wake variant is present at all (exact) or fuzzy within edit distance 1
  const variants = getEffectiveVariants();
  const hasWakeWord = variants.some(w => lower.includes(w)) || hasFuzzyWake(lower);
  if (!hasWakeWord) {
    // STRICT GATE — owner spec 2026-05-01: "Aria doesn't start using
    // credits until she hears her name."
    //
    // Previously this branch had a phrasing-based fallback that fired
    // for any question-shaped text ("what time is it", "tell me about
    // X") even without the wake word. That caused Aria to respond to
    // ambient TV / nearby conversation / a roommate asking about
    // something. Costs real API tokens for a turn the user never
    // initiated.
    //
    // Now: when the wake word isn't present, we ONLY allow follow-up
    // phrasing (yeah/no/please/thanks/short replies) WHILE Aria is
    // already attentive and mid-conversation. In sleeping mode, the
    // user MUST say "Aria" — full stop.
    // Attentive mid-conversation: short follow-ups ("yeah", "thanks",
    // "what about tomorrow") and full-question/command phrasing all
    // count as directed. Sleeping mode requires the literal name.
    if (ariaAttentionState === 'attentive') {
      if (isDirectedByPhrasing(text)) {
        return { isDirected: true, confidence: 0.6, reason: 'attentive_directed_phrasing', isDelegation: false };
      }
      if (FOLLOWUP_PATTERNS.some((re) => re.test(text.trim()))) {
        return { isDirected: true, confidence: 0.55, reason: 'attentive_followup', isDelegation: false };
      }
    }
    return { isDirected: false, confidence: 0, reason: 'no_wake_word', isDelegation: false };
  }

  // Score directed vs. not-directed
  let directedScore = 0;
  let directedReason = '';
  let notDirectedScore = 0;
  let notDirectedReason = '';
  let isDelegation = false;

  for (const pattern of getDirectedPatterns()) {
    if (pattern.re.test(lower)) {
      if (pattern.weight > directedScore) {
        directedScore = pattern.weight;
        directedReason = pattern.reason;
      }
      if (pattern.reason === 'delegation') {
        isDelegation = true;
      }
    }
  }

  for (const pattern of getNotDirectedPatterns()) {
    if (pattern.re.test(lower)) {
      if (pattern.weight > notDirectedScore) {
        notDirectedScore = pattern.weight;
        notDirectedReason = pattern.reason;
      }
    }
  }

  // Decision logic
  const margin = 0.3;

  // Clear directed signal
  if (directedScore > 0 && directedScore > notDirectedScore + margin) {
    return {
      isDirected: true,
      confidence: directedScore,
      reason: directedReason,
      isDelegation,
    };
  }

  // Clear not-directed signal
  if (notDirectedScore > 0 && notDirectedScore > directedScore + margin) {
    return {
      isDirected: false,
      confidence: notDirectedScore,
      reason: notDirectedReason,
      isDelegation: false,
    };
  }

  // Both matched or ambiguous — use attention state as tiebreaker
  if (directedScore > 0 && notDirectedScore > 0) {
    // When attentive (in conversation), lean toward directed
    if (ariaAttentionState === 'attentive') {
      return {
        isDirected: directedScore >= notDirectedScore - 0.1,
        confidence: Math.max(directedScore, notDirectedScore) * 0.7,
        reason: `ambiguous_leaning_${directedScore >= notDirectedScore - 0.1 ? 'directed' : 'not_directed'}`,
        isDelegation,
      };
    }
    // When sleeping, lean toward not-directed (err quiet)
    return {
      isDirected: false,
      confidence: notDirectedScore * 0.7,
      reason: 'ambiguous_sleeping_err_quiet',
      isDelegation: false,
    };
  }

  // Only directed matched (even weakly)
  if (directedScore > 0) {
    return { isDirected: true, confidence: directedScore, reason: directedReason, isDelegation };
  }

  // Only not-directed matched
  if (notDirectedScore > 0) {
    return { isDirected: false, confidence: notDirectedScore, reason: notDirectedReason, isDelegation: false };
  }

  // Neither matched well — "Aria" is in the text but context unclear
  // Default: directed if Aria just appears with basic surrounding words
  if (ariaAttentionState === 'attentive') {
    return { isDirected: true, confidence: 0.5, reason: 'default_attentive', isDelegation: false };
  }

  return { isDirected: false, confidence: 0.3, reason: 'default_sleeping', isDelegation: false };
}

/**
 * Check if an utterance looks like a valid follow-up (no wake word needed).
 * Used during the engagement window when Aria is attentive.
 *
 * @param {string} transcript
 * @returns {boolean}
 */
export function isLikelyFollowUp(transcript) {
  if (!transcript) return false;
  const trimmed = transcript.trim();
  // Very short replies are likely responses to Aria's question
  if (trimmed.split(/\s+/).length <= 4) return true;
  return FOLLOWUP_PATTERNS.some(re => re.test(trimmed));
}

/**
 * Strip the wake word from the beginning of a transcript so the response
 * feels conversational ("Aria what time is it" → "what time is it").
 *
 * @param {string} transcript
 * @returns {string}
 */
export function stripWakeWord(transcript) {
  if (!transcript) return '';
  let text = transcript.trim();
  const variants = getEffectiveVariants();
  // Remove prefix + wake word: "Hey Aria, ..." → "..."
  for (const prefix of WAKE_PREFIXES) {
    for (const variant of variants) {
      const pattern = new RegExp(`^${prefix}\\s+${variant}[,.\\s!]*\\s*`, 'i');
      text = text.replace(pattern, '');
    }
  }
  // Remove solo wake word at start: "Aria, ..." → "..."
  for (const variant of variants) {
    const pattern = new RegExp(`^${variant}[,.\\s!]*\\s*`, 'i');
    text = text.replace(pattern, '');
  }
  return text.trim();
}

// ── Interrupt detection (client-side, mirrors ariaIntentGate.js) ─────────────
const INTERRUPT_WORDS = new Set([
  'stop', 'cancel', 'pause', 'shut up', 'quiet', 'never mind', 'nevermind',
  "that's enough", 'be quiet', 'hush', 'enough', 'hold on',
  'wait', 'no no no', 'stop stop', 'cancel that', 'stop talking',
  'stop it', 'okay stop', 'ok stop', 'aria stop', 'hey stop',
]);

/**
 * Check if an utterance is an explicit interrupt command.
 * Used client-side to decide whether to stop Aria's TTS playback.
 * Only these explicit stop commands should cut Aria off — normal speech should not.
 *
 * @param {string} text
 * @returns {boolean}
 */
export function isInterruptCommand(text) {
  if (!text) return false;
  const lower = text.toLowerCase().trim();
  if (INTERRUPT_WORDS.has(lower)) return true;
  // Match if utterance starts with an interrupt word, but exclude
  // cases where the interrupt word is part of a longer phrase
  // (e.g., "stop by the store" is NOT an interrupt)
  const NON_INTERRUPT_CONTINUATIONS = /^(?:by|at|in|on|for|the|a|an|to|and|or|with|from|about|over|around|after|before|it\s+from|that\s+from)\b/i;
  for (const word of INTERRUPT_WORDS) {
    if (lower.startsWith(word + ' ') || lower.startsWith(word + ',') || lower.startsWith(word + '.')) {
      const rest = lower.slice(word.length).trim().replace(/^[,.]/, '').trim();
      if (rest && NON_INTERRUPT_CONTINUATIONS.test(rest)) continue;
      return true;
    }
  }
  return false;
}

// Backwards-compat export name. Now returns the effective list (defaults
// merged with any user-learned variants) so call sites don't need to know
// about the new function. New code should call getEffectiveVariants() directly.
export const WAKE_VARIANTS = DEFAULT_WAKE_VARIANTS;
export { FOLLOWUP_PATTERNS, isDirectedByPhrasing, getEffectiveVariants };
